// Syntax highlighting of code blocks. Display only: the markdown (```lang) is
// unchanged, so the editor package (shared schema, headless parse and
// serialize, the server) is not touched; the screen replaces the code block
// extension by name.
//
// `@tiptap/extension-code-block-lowlight` is `CodeBlock.extend({ addOptions,
// addProseMirrorPlugins })`: the node name (`codeBlock`), the `language`
// attribute and the markdown mapping are the plain CodeBlock's. It adds only a
// plugin laying inline decorations for the highlighting, so the schema stays
// equal to the shared one (round trips, Yjs and the block id attribute keep
// working).
import type { NodeViewRenderer } from "@tiptap/core";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import { common, createLowlight } from "lowlight";
import type { DocumentBlockContext, DocumentBlockPart } from "./block-parts";
import { codeBlockCopyView } from "./code-copy";

/**
 * lowlight's `common` set (highlight.js's ~37 usual languages); `all` (190+)
 * would bloat the bundle. Made once for the module: registering languages is a
 * side effect on the highlight.js instance, not to be repeated per editor.
 */
const lowlight = createLowlight(common);
// The `ui` language is tag shaped: colored as xml. A language without a
// registration falls to highlightAuto, which tries all ~37 - most of an
// editor's set-up time for a document with ui blocks, over a second on phones.
lowlight.registerAlias({ xml: ["ui"] });

/** The highlighted code block with its copy band (code-copy.ts). Same name and attributes as CodeBlock. */
export const CodeBlockWithHighlight = CodeBlockLowlight.extend({
  addNodeView() {
    return codeBlockCopyView;
  },
}).configure({ lowlight });

type Claim = NonNullable<DocumentBlockPart["codeBlock"]>;

const languageOf = (node: { attrs: Record<string, unknown> }): unknown =>
  node.attrs.language;

/**
 * The code block's node view: a part's view for its language, the copy band
 * otherwise. When the language changes across that line (ui -> js), `update`
 * returns false so ProseMirror makes the view again with the other kind.
 */
function viewByLanguage(
  claims: readonly Claim[],
  ctx: DocumentBlockContext,
): NodeViewRenderer {
  const views = claims.map((c) => ({
    language: c.language,
    view: c.view(ctx),
  }));
  const pick = (node: { attrs: Record<string, unknown> }) =>
    views.find((v) => v.language === languageOf(node)) ?? null;
  return (props) => {
    const claimed = pick(props.node);
    const view = claimed ? claimed.view(props) : codeBlockCopyView(props);
    const update = view.update?.bind(view);
    view.update = (next, ...rest) =>
      pick(next) === claimed && (update ? update(next, ...rest) : false);
    return view;
  };
}

/**
 * The document body's code block: highlighted, with the parts' views for
 * their languages and their keys (a part's key runs first and falls through
 * to the default when it returns false). Without parts it is the plain one.
 */
export function codeBlockWithParts(
  parts: readonly DocumentBlockPart[],
  ctx: DocumentBlockContext,
) {
  const claims = parts.flatMap((p) => (p.codeBlock ? [p.codeBlock] : []));
  if (claims.length === 0) return CodeBlockWithHighlight;
  return CodeBlockLowlight.extend({
    addNodeView() {
      return viewByLanguage(claims, ctx);
    },
    addKeyboardShortcuts() {
      const keys = { ...this.parent?.() };
      for (const claim of claims)
        for (const [key, run] of Object.entries(claim.keys ?? {})) {
          const before = keys[key];
          keys[key] = (props) => run(this.editor) || (before?.(props) ?? false);
        }
      return keys;
    },
  }).configure({ lowlight });
}
